import {useEffect, useRef, useState} from "react";

import {CHART_HEIGHT, CHART_WIDTH, ResponseTimeSparkline, ResponseTimeSparklineProps} from "./ResponseTimeSparkline";

// One IntersectionObserver shared by every placeholder on the page. A status page can carry ~1000
// components; a per-row observer would allocate ~1000 observers before any chart mounts, which
// eats into the very win this lazy-mount is meant to deliver. All rows use the same options, so a
// single observer with a Map of element → "reveal" callback covers them all.
const REVEAL_MARGIN = "200px";
let sharedObserver: IntersectionObserver | null = null;
const revealCallbacks = new Map<Element, () => void>();

function getSharedObserver(): IntersectionObserver | null {
  if (typeof IntersectionObserver === "undefined") return null;
  if (!sharedObserver) {
    sharedObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const reveal = revealCallbacks.get(entry.target);
          if (reveal) {
            revealCallbacks.delete(entry.target);
            sharedObserver!.unobserve(entry.target);
            reveal();
          }
        }
      },
      // Start rendering a little before the row enters the viewport so it's ready on arrival.
      {rootMargin: REVEAL_MARGIN}
    );
  }
  return sharedObserver;
}

function observeOnce(element: Element, reveal: () => void): () => void {
  const observer = getSharedObserver();
  if (!observer) {
    reveal();
    return () => {};
  }
  revealCallbacks.set(element, reveal);
  observer.observe(element);
  return () => {
    revealCallbacks.delete(element);
    observer.unobserve(element);
  };
}

/**
 * Renders a ResponseTimeSparkline only once its row is scrolled into view.
 *
 * Deferring each ApexCharts instance until it nears the viewport (and keeping it mounted
 * thereafter — re-initialising on every scroll would churn) is what keeps a 1000-component page
 * responsive. The placeholder reserves the chart's footprint so layout doesn't jump.
 */
export const LazyResponseTimeSparkline = (props: ResponseTimeSparklineProps) => {
  const ref = useRef<HTMLDivElement>(null);
  // Where IntersectionObserver isn't available (tests, SSR, very old browsers), render eagerly.
  const [inView, setInView] = useState(typeof IntersectionObserver === "undefined");

  useEffect(() => {
    if (inView) return;
    const element = ref.current;
    if (!element) return;
    return observeOnce(element, () => setInView(true));
  }, [inView]);

  return (
    <div ref={ref} style={{width: CHART_WIDTH, height: CHART_HEIGHT}}>
      {inView ? <ResponseTimeSparkline {...props} /> : null}
    </div>
  );
};
